/**
 * Marqueurs d'évènements : découpage par période et lecture de leur effet.
 *
 * ── Ce que ce fichier prétend faire, et ce qu'il ne prétend pas ─────────────
 * Il compare la moyenne journalière d'une métrique sur les 14 jours qui
 * précèdent un marqueur à celle des 14 jours qui le suivent. C'est un
 * avant/après, PAS une mesure causale : rien n'isole l'effet du changement de
 * celui d'une campagne simultanée, d'une vague de vacances scolaires ou d'une
 * saisonnalité. Le verdict répond à « la métrique a-t-elle bougé, et dans quel
 * sens », et c'est déjà ce qui manquait pour lire une courbe.
 *
 * Trois garde-fous, parce qu'un avant/après naïf conclut n'importe quoi :
 *
 *  1. `MARKER_IMPACT_MIN_DAYS` jours mesurés de chaque côté, au minimum. En
 *     dessous, la moyenne se joue sur deux points et le moindre jour creux
 *     inverse la conclusion. On répond « pas assez de recul » plutôt qu'un
 *     chiffre.
 *
 *  2. Fenêtre de 14 jours et non 7 : l'activité est nettement plus faible le
 *     week-end (cf. la heatmap). Une fenêtre qui ne couvre pas un nombre entier
 *     de semaines compare six jours ouvrés à huit, et attribue l'écart au
 *     changement.
 *
 *  3. `MARKER_IMPACT_NEUTRAL_PERCENT` de variation minimum pour conclure. En
 *     dessous, on affiche « stable » : d'une quinzaine à l'autre, l'activité
 *     bouge de quelques pourcents sans qu'il se soit rien passé.
 *
 * ── Niveau ou pente : deux natures de séries ────────────────────────────────
 * Comparer les moyennes marche pour une série de FLUX (actions par jour, taux
 * de pénétration). Sur un CUMUL (nombre de comptes, nombre d'abonnés), ça ne
 * veut rien dire : le total monte toujours, et la moyenne d'après est
 * mécaniquement plus haute même si les inscriptions se sont arrêtées net.
 *
 * D'où le mode `growth`, qui compare les incréments journaliers — c'est-à-dire
 * la pente. Un cumul qui ralentit y ressort en baisse, ce qui est la vérité.
 */

import {
  MARKER_IMPACT_MIN_DAYS,
  MARKER_IMPACT_NEUTRAL_PERCENT,
  MARKER_IMPACT_WINDOW_DAYS,
  type Marker,
} from '@shared/types/stats';

import { shiftDay } from './dates';
import { formatDecimal, formatPercent } from './format';
import { impactTone, verdictGlyph, verdictLabel, type ImpactTone, type ImpactVerdict } from './verdict';

// Le vocabulaire de direction vit dans `lib/verdict.ts` — il sert aussi aux
// tuiles de KPI. Ré-exporté ici pour que les appelants du module marqueurs
// n'aient qu'un seul point d'entrée.
export { impactTone, verdictGlyph, verdictLabel };
export type { ImpactTone, ImpactVerdict };

/** Un point de série, réduit à ce dont le calcul a besoin. */
export interface SeriesPoint {
  date: string;
  /** `null` = jour non mesuré. Jamais 0 pour dire « pas de mesure ». */
  value: number | null;
}

/**
 * Nature de la série, qui décide de ce qu'on compare.
 *
 *  · `level`  — série de flux (actions/jour, taux). On compare les moyennes.
 *  · `growth` — série cumulée (comptes, abonnés). On compare les incréments
 *               journaliers, sinon le verdict est toujours « en hausse ».
 */
export type ImpactMode = 'level' | 'growth';

/** Unité de la métrique, qui décide de la façon d'annoncer l'écart. */
export type ImpactUnit = 'count' | 'percent';

export interface MarkerImpact {
  verdict: ImpactVerdict;
  /** Moyenne journalière avant le marqueur. `null` si pas assez de jours. */
  before: number | null;
  /** Moyenne journalière à partir du jour du marqueur, inclus. */
  after: number | null;
  /** Écart absolu, dans l'unité de la série. */
  deltaAbsolute: number | null;
  /** Variation relative en %. `null` quand la moyenne d'avant est nulle. */
  deltaPercent: number | null;
  daysBefore: number;
  daysAfter: number;
}

/** Les marqueurs tombant dans la période affichée, du plus ancien au plus récent. */
export function markersInPeriod(
  markers: readonly Marker[],
  from: string,
  to: string,
): Marker[] {
  // Comparaison lexicographique : valide sur du YYYY-MM-DD zero-padde.
  return markers
    .filter((marker) => marker.date >= from && marker.date <= to)
    .sort((a, b) => (a.date === b.date ? a.id - b.id : a.date.localeCompare(b.date)));
}

/** Extrait une série exploitable d'un tableau de points d'API. */
export function seriesFrom<T extends { date: string }>(
  rows: readonly T[],
  value: (row: T) => number | null,
): SeriesPoint[] {
  return rows.map((row) => ({ date: row.date, value: value(row) }));
}

/**
 * Passe d'un cumul à ses incréments journaliers.
 *
 * Un incrément n'est calculé QUE entre deux jours civils consécutifs tous deux
 * mesurés. Enjamber un trou donnerait le gain de trois jours attribué à un
 * seul, soit un pic de croissance là où il n'y a qu'un snapshot manquant.
 *
 * Le premier point n'a pas de prédécesseur : il sort à `null`, et ne compte
 * donc pas dans les jours mesurés.
 */
export function dailyIncrements(series: readonly SeriesPoint[]): SeriesPoint[] {
  return series.map((point, index) => {
    const previous = index > 0 ? series[index - 1] : undefined;

    if (
      !previous ||
      previous.value === null ||
      point.value === null ||
      shiftDay(previous.date, 1) !== point.date
    ) {
      return { date: point.date, value: null };
    }

    return { date: point.date, value: point.value - previous.value };
  });
}

/** Moyenne des valeurs mesurées d'une tranche, et le nombre de jours retenus. */
function meanOfWindow(
  series: readonly SeriesPoint[],
  fromDate: string,
  toDate: string,
): { mean: number | null; days: number } {
  let sum = 0;
  let days = 0;

  for (const point of series) {
    if (point.date < fromDate || point.date > toDate || point.value === null) continue;
    sum += point.value;
    days += 1;
  }

  return { mean: days > 0 ? sum / days : null, days };
}

/**
 * Effet constaté autour d'un marqueur.
 *
 * Le jour du marqueur appartient à l'APRÈS : une mise à jour publiée le 12 est
 * en ligne le 12. La fenêtre d'avant s'arrête donc la veille.
 *
 * Les deux fenêtres sont bornées par la série fournie, donc par la période
 * affichée : un marqueur posé au bord de la période ressort `insufficient`
 * jusqu'à ce qu'on élargisse la période. C'est voulu — il n'y a rien à
 * comparer, et inventer un verdict sur trois jours serait pire que se taire.
 */
export function computeImpact(
  series: readonly SeriesPoint[],
  markerDate: string,
  mode: ImpactMode,
): MarkerImpact {
  const effective = mode === 'growth' ? dailyIncrements(series) : series;

  const beforeWindow = meanOfWindow(
    effective,
    shiftDay(markerDate, -MARKER_IMPACT_WINDOW_DAYS),
    shiftDay(markerDate, -1),
  );
  const afterWindow = meanOfWindow(
    effective,
    markerDate,
    shiftDay(markerDate, MARKER_IMPACT_WINDOW_DAYS - 1),
  );

  const insufficient =
    beforeWindow.days < MARKER_IMPACT_MIN_DAYS ||
    afterWindow.days < MARKER_IMPACT_MIN_DAYS ||
    beforeWindow.mean === null ||
    afterWindow.mean === null;

  if (insufficient) {
    return {
      verdict: 'insufficient',
      before: beforeWindow.mean,
      after: afterWindow.mean,
      deltaAbsolute: null,
      deltaPercent: null,
      daysBefore: beforeWindow.days,
      daysAfter: afterWindow.days,
    };
  }

  const before = beforeWindow.mean as number;
  const after = afterWindow.mean as number;
  const deltaAbsolute = after - before;

  // Division par la valeur ABSOLUE : sur un mode `growth`, une pente d'avant
  // negative (des abonnes perdus) inverserait le signe de la variation, et une
  // amelioration s'afficherait en baisse.
  const deltaPercent = before !== 0 ? (deltaAbsolute / Math.abs(before)) * 100 : null;

  let verdict: ImpactVerdict;
  if (deltaPercent === null) {
    // Moyenne d'avant nulle : aucune variation relative n'a de sens, mais le
    // sens du mouvement, lui, est parfaitement lisible.
    verdict = deltaAbsolute > 0 ? 'up' : deltaAbsolute < 0 ? 'down' : 'flat';
  } else if (Math.abs(deltaPercent) < MARKER_IMPACT_NEUTRAL_PERCENT) {
    verdict = 'flat';
  } else {
    verdict = deltaPercent > 0 ? 'up' : 'down';
  }

  return {
    verdict,
    before,
    after,
    deltaAbsolute,
    deltaPercent,
    daysBefore: beforeWindow.days,
    daysAfter: afterWindow.days,
  };
}

/**
 * Abscisse à laquelle poser le trait sur le graphique.
 *
 * L'axe des X est CATÉGORIEL (`dataKey="date"`) : Recharts ne sait placer une
 * `ReferenceLine` que sur une catégorie existante. Un marqueur daté d'un jour
 * absent de la série — un jour sans snapshot, ou sans aucune action — ne se
 * dessinerait pas du tout.
 *
 * On le cale donc sur le premier jour mesuré à partir de sa date. Le trait peut
 * ainsi apparaître un ou deux jours à droite de l'évènement ; c'est visible et
 * assumé, là où l'absence de trait serait silencieuse. `null` si la série ne
 * contient plus aucun jour après cette date.
 */
export function resolveMarkerX(
  series: readonly SeriesPoint[],
  markerDate: string,
): string | null {
  for (const point of series) {
    if (point.date >= markerDate) return point.date;
  }
  return null;
}

// -----------------------------------------------------------------------------
// Mise en forme du verdict
// -----------------------------------------------------------------------------

/**
 * Écart avant/après, sous la forme qui convient à l'unité.
 *
 * Un taux s'annonce en POINTS et non en variation relative : « la pénétration
 * a gagné 8 % » se lit comme 8 points par la moitié des lecteurs, alors qu'il
 * s'agirait de 1,7 point sur une base de 21 %. Les quantités, elles, s'annoncent
 * en relatif — c'est ce qui rend deux marqueurs comparables entre eux.
 */
export function formatImpactDelta(impact: MarkerImpact, unit: ImpactUnit): string {
  if (impact.verdict === 'insufficient') return '—';

  if (unit === 'percent') {
    const points = impact.deltaAbsolute ?? 0;
    return `${points >= 0 ? '+' : '−'}${formatDecimal(Math.abs(points))} pt`;
  }

  if (impact.deltaPercent === null) {
    // Moyenne d'avant nulle : la variation relative n'existe pas, on annonce
    // l'ecart brut plutot qu'un tiret.
    const absolute = impact.deltaAbsolute ?? 0;
    return `${absolute >= 0 ? '+' : '−'}${formatDecimal(Math.abs(absolute))}`;
  }

  return `${impact.deltaPercent >= 0 ? '+' : '−'}${formatPercent(Math.abs(impact.deltaPercent))}`;
}

/** Moyennes avant/après en clair, pour l'infobulle et la vue tableau. */
export function formatImpactWindows(impact: MarkerImpact, unit: ImpactUnit): string {
  if (impact.before === null || impact.after === null) {
    return `${impact.daysBefore} j mesurés avant, ${impact.daysAfter} j après`;
  }

  const render = (value: number) =>
    unit === 'percent' ? formatPercent(value) : formatDecimal(value);

  return `${render(impact.before)} → ${render(impact.after)} par jour (${impact.daysBefore} j / ${impact.daysAfter} j)`;
}
